import { isAppError } from '@shared/errors';
import { enqueueSnackbar } from 'notistack';

/**
 * Extract a user-safe message from any thrown value (TAS-3245):
 * AppError → its `userMessage`; a string → itself; a plain Error → its message
 * (legacy); anything else → a generic fallback. Never surfaces a raw non-Error.
 */
export function toUserMessage(error: unknown): string {
  if (isAppError(error)) return error.userMessage;
  if (typeof error === 'string' && error.trim()) return error;
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong. Please try again.';
}

/**
 * Thin wrappers over notistack so call sites stop hand-rolling `enqueueSnackbar`.
 * `notify.error` accepts a string OR a thrown value (AppError/Error) and shows
 * the user-safe message. Rich error content (referenceSlug, suggestion) + the
 * Apollo ErrorLink auto-toast land in TAS-3247.
 */
export const notify = {
  success: (message: string) => enqueueSnackbar(message, { variant: 'success' }),
  info: (message: string) => enqueueSnackbar(message, { variant: 'info' }),
  warn: (message: string) => enqueueSnackbar(message, { variant: 'warning' }),
  error: (messageOrError: unknown) => enqueueSnackbar(toUserMessage(messageOrError), { variant: 'error' }),
};
